import React, { useState } from 'react';
import { RefreshCw, Mail, Loader2, Eye, EyeOff, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { mailAPI } from '../lib/api';
import { Button } from './ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';

export const ResyncButton = ({ accountId, onResyncComplete }) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const handleResync = async () => {
        try {
            setLoading(true);
            setOpen(false);
            const response = await mailAPI.resyncZimbraAccount(accountId);
            toast.success(
                `Re-sync Completed`,
                {
                    description: `${response.data.synced_emails || 0} emails synced successfully`,
                }
            );
            if (onResyncComplete) onResyncComplete();
        } catch (error) {
            const detail = error.response?.data?.detail || 'Failed to re-sync account. Please try again.';
            toast.error(`Re-sync Failed`, {
                description: detail,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Syncing...' : 'Re-sync'}
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Re-sync Account?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will clear local cache and re-sync all emails from IMAP.
                            This may take a few minutes.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setOpen(false)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleResync}>
                            Yes, Re-sync
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </React.Fragment>
    );
};

export const TestSMTPButton = ({ accountId }) => {
    const [loading, setLoading] = useState(false);

    const handleTestSMTP = async () => {
        try {
            setLoading(true);
            const response = await mailAPI.testZimbraSMTP(accountId);
            if (response.data.status === 'success') {
                toast.success(`SMTP Test Successful`, {
                    description: 'Connection established. Test email sent.',
                });
            } else {
                toast.error(`SMTP Test Failed`, {
                    description: response.data.message || 'Connection failed',
                });
            }
        } catch (error) {
            const detail = error.response?.data?.detail || 'Failed to test SMTP connection';
            toast.error(`SMTP Test Failed`, {
                description: detail,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={handleTestSMTP}
            disabled={loading}
        >
            <Mail className="w-4 h-4 mr-2" />
            {loading ? 'Testing...' : 'Test SMTP'}
        </Button>
    );
};

export const ShowPasswordButton = ({ accountId }) => {
    const [open, setOpen] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [revealedEmail, setRevealedEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleReveal = async () => {
        if (!confirmPassword.trim()) {
            toast.error('Masukkan password akun Anda');
            return;
        }
        try {
            setLoading(true);
            const response = await mailAPI.revealZimbraPassword(accountId, confirmPassword);
            if (response.data) {
                setRevealedPassword(response.data.password);
                setRevealedEmail(response.data.email);
            }
        } catch (error) {
            const detail = error.response?.data?.detail || 'Gagal mengambil password';
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Password disalin ke clipboard');
        });
    };

    const closeDialog = () => {
        setOpen(false);
        setRevealedPassword(null);
        setConfirmPassword('');
        setShowPassword(false);
    };

    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                <KeyRound className="w-4 h-4 mr-2" />
                Show Password
            </Button>
            <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5" />
                            Lihat Password Zimbra
                        </DialogTitle>
                        <DialogDescription>
                            {revealedPassword
                                ? `Password untuk akun ${revealedEmail}`
                                : 'Masukkan password akun DigiTask Anda untuk melihat password Zimbra.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {revealedPassword ? (
                        <div className="space-y-4">
                            <div className="bg-muted border rounded-lg p-4">
                                <p className="text-xs text-brand-text-secondary mb-1">Email Zimbra</p>
                                <p className="font-mono text-sm font-semibold">{revealedEmail}</p>
                            </div>
                            <div className="bg-muted border rounded-lg p-4">
                                <p className="text-xs text-brand-text-secondary mb-1">Password</p>
                                <div className="flex items-center gap-2">
                                    <p className="font-mono text-sm font-semibold flex-1 break-all">
                                        {showPassword ? revealedPassword : '••••••••••••'}
                                    </p>
                                    <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(revealedPassword)}>
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            <p className="text-xs text-orange-600 flex items-center gap-1">
                                <KeyRound className="w-3 h-3" />
                                Simpan password ini dengan aman. Password tidak akan ditampilkan lagi setelah dialog ditutup.
                            </p>
                            <DialogFooter>
                                <Button variant="outline" onClick={closeDialog}>Tutup</Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Password DigiTask Anda</label>
                                <Input
                                    type="password"
                                    placeholder="Masukkan password akun Anda"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleReveal(); }}
                                />
                                <p className="text-xs text-brand-text-tertiary">
                                    Untuk keamanan, masukkan password login DigiTask Anda untuk memverifikasi identitas.
                                </p>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={closeDialog}>Batal</Button>
                                <Button onClick={handleReveal} disabled={loading || !confirmPassword.trim()}>
                                    {loading ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Memverifikasi...</>
                                    ) : (
                                        <><Eye className="w-4 h-4 mr-2" />Lihat Password</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export const ZimbraAccountActions = ({ accountId, onResyncComplete }) => {
    if (!accountId) return null;

    return (
        <div className="flex gap-2 mt-2">
            <ResyncButton accountId={accountId} onResyncComplete={onResyncComplete} />
            <TestSMTPButton accountId={accountId} />
            <ShowPasswordButton accountId={accountId} />
        </div>
    );
};

export default ZimbraAccountActions;